/**
 * Core Companion tools — COPILOT_MASTER_PLAN subsystem C.
 *
 * The verbs the Companion / agent-runner invoke, each WRAPPING an existing
 * executor (no re-implementation):
 *   navigate         → action-bridge navigate
 *   openForm         → create-registry + requestOpenCreate (openCreateForm recipe)
 *   fillForm         → openAndFillForm recipe (HITL diff → RHF/appliers)
 *   createRecord     → agent-runner backend-write recipe (HITL-gated POST)
 *   updateRecord     → tool-executor PUT/PATCH (HITL-gated)
 *   startWorkflow    → durable agentic task OR MAS plan (subsystem D)
 *   stopWorkflow     → DELETE durable task / cancel MAS plan
 *   queryData        → ml-tools.service queryData (data-QA → nl-query fallback)
 *   recallTasks      → copilot-tasks.service list
 *   notify           → ml-tools.service sendNotification
 *   analyzeCurrentPage → readPageContext + entity summary
 *
 * `mode`/`hitl` mirror the server allow-list side-effect classes (subsystem H);
 * the registry enforces the actual gating.
 */

import { executeAction, readPageContext } from '../action-bridge'
import { openCreateForm, runPlanStep } from '../agent-runner'
import { openAndFillForm } from '../agent-runner'
import { resolveCreateEntryOrNull, getCreateEntryByModule } from '../create-registry'
import { hitlBackendTool } from '../hitl'
import type { CompanionTool, ToolContext, ToolRunResult } from './types'
import {
  createMasPlan,
  startMasPlan,
  controlMasPlan,
  queryData as queryDataSvc,
  sendNotification,
} from '@/services/ml-tools.service'
import { createAgenticTask } from '@/services/ai-hub.service'
import { fetchCompanionTasks, cancelCompanionTask } from '@/services/copilot-tasks.service'
import { createMasPlanForGoal } from '../runtime-router'

function s(v: unknown, fallback = ''): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback
}

// ── navigate ──────────────────────────────────────────────────────────────────

const navigate: CompanionTool = {
  name: 'navigate',
  description: 'Naviguer vers une page',
  argsSchema: [{ name: 'to', type: 'string', required: true, description: 'Route SPA (ex: /risks)' }],
  mode: 'read',
  hitl: false,
  surfaces: ['web'],
  group: 'core',
  run: async (args) => {
    const to = s(args.to ?? args.route ?? args.path)
    if (!to) return { ok: false, detail: 'navigate sans cible' }
    const res = await executeAction({ op: 'navigate', to })
    return { ok: res.ok, detail: res.detail, output: { to } }
  },
}

// ── openForm ──────────────────────────────────────────────────────────────────

const openForm: CompanionTool = {
  name: 'openForm',
  description: 'Ouvrir un formulaire de création',
  argsSchema: [
    { name: 'module', type: 'string', description: 'Module ou intention (risque, politique, contrôle…)' },
    { name: 'formId', type: 'string' },
  ],
  mode: 'read',
  hitl: false,
  surfaces: ['web'],
  group: 'core',
  run: async (args, ctx) => {
    const formId = s(args.formId ?? args.form_id)
    // When no explicit formId, resolve the create surface from the module/intent.
    const entry =
      resolveCreateEntryOrNull(s(args.module ?? args.intent ?? ctx.module), ctx.module) ??
      getCreateEntryByModule(ctx.module) ??
      null
    const targetFormId = formId || entry?.formId
    if (!targetFormId) {
      return { ok: false, detail: 'Module de création non reconnu (précisez : risque, politique, contrôle…).' }
    }
    if (entry && typeof window !== 'undefined' && window.location.pathname !== entry.route) {
      await executeAction({ op: 'navigate', to: entry.route })
    }
    const opened = await openCreateForm(targetFormId, 9000)
    return {
      ok: opened,
      detail: opened ? `Formulaire « ${targetFormId} » ouvert` : `Impossible d’ouvrir « ${targetFormId} »`,
      output: { formId: targetFormId },
    }
  },
}

// ── fillForm ──────────────────────────────────────────────────────────────────

const fillForm: CompanionTool = {
  name: 'fillForm',
  description: 'Pré-remplir un formulaire par l’IA (avec confirmation)',
  argsSchema: [
    { name: 'module', type: 'string', description: 'Module ou intention' },
    { name: 'prompt', type: 'string', description: 'Description en langage naturel des valeurs' },
  ],
  mode: 'write',
  hitl: true, // always shows the HITL diff before applying values
  surfaces: ['web'],
  group: 'core',
  run: async (args, ctx) => {
    const prompt = s(args.prompt ?? args.description ?? args.values_text)
    const module = s(args.module ?? ctx.module, ctx.module ?? '')
    if (!prompt) return { ok: false, detail: 'fillForm sans description des valeurs' }
    // openAndFillForm owns the navigate → open → fill-form → HITL diff → apply flow.
    await openAndFillForm(module || undefined, prompt)
    return { ok: true, detail: 'Formulaire pré-rempli — vérifiez puis enregistrez.' }
  },
}

// ── createRecord ──────────────────────────────────────────────────────────────

const createRecord: CompanionTool = {
  name: 'createRecord',
  description: 'Créer un enregistrement (avec confirmation)',
  argsSchema: [
    { name: 'entity_type', type: 'string', required: true },
    { name: 'values', type: 'object', required: true },
  ],
  mode: 'write',
  hitl: true,
  surfaces: ['web'],
  group: 'core',
  run: async (args, ctx) => {
    // Reuse the agent-runner backend-write recipe (endpoint resolution +
    // field-map + HITL gate) by shaping a createRecord plan step.
    return runPlanStep(
      {
        step: 0,
        op: 'createRecord',
        args: {
          entity_type: args.entity_type ?? args.module,
          module: args.module ?? ctx.module,
          fields: args.values ?? args.fields ?? args.body ?? {},
        },
        side_effect: 'write',
        confirm: true,
      },
      {},
      { module: ctx.module, pageContext: ctx.pageContext },
    )
  },
}

// ── updateRecord ──────────────────────────────────────────────────────────────

const updateRecord: CompanionTool = {
  name: 'updateRecord',
  description: 'Mettre à jour un enregistrement (avec confirmation)',
  argsSchema: [
    { name: 'path', type: 'string', required: true, description: 'Endpoint (ex: /risk-pro/risks/{id})' },
    { name: 'values', type: 'object', required: true },
    { name: 'method', type: 'string', enum: ['PUT', 'PATCH'] },
  ],
  mode: 'write',
  hitl: true,
  surfaces: ['web'],
  group: 'core',
  run: async (args, ctx): Promise<ToolRunResult> => {
    const path = s(args.path ?? args.endpoint)
    if (!path) return { ok: false, detail: 'updateRecord sans endpoint' }
    const method = s(args.method, 'PATCH').toUpperCase()
    const body = (args.values ?? args.fields ?? args.body ?? {}) as Record<string, unknown>
    const result = await hitlBackendTool(
      {
        capability_code: `record.update`,
        method: method === 'PUT' ? 'PUT' : 'PATCH',
        path,
        body,
        side_effect: 'write',
        description: `Mettre à jour ${path}`,
      },
      { system: `${method} ${path}` },
    )
    if (result === null) return { ok: false, cancelled: true, detail: 'Annulé' }
    if (!result.ok) return { ok: false, detail: `Échec ${result.status} : ${result.error ?? 'erreur'}` }
    const data = (result.data ?? {}) as Record<string, unknown>
    return { ok: true, detail: `Enregistrement mis à jour (${result.status})`, output: { ...data } }
  },
}

// ── startWorkflow / stopWorkflow (subsystem D) ────────────────────────────────

const startWorkflow: CompanionTool = {
  name: 'startWorkflow',
  description: 'Lancer une tâche en arrière-plan (agent durable / MAS)',
  argsSchema: [
    { name: 'goal', type: 'string', required: true },
    { name: 'domain', type: 'string' },
    { name: 'runtime', type: 'string', enum: ['durable', 'mas'] },
  ],
  mode: 'write',
  hitl: false, // launching a background plan shows a confirmable toast in agent mode; HITL parks inside it
  surfaces: ['web'],
  group: 'core',
  run: async (args, ctx): Promise<ToolRunResult> => {
    const goal = s(args.goal ?? args.skill_id)
    if (!goal) return { ok: false, detail: 'startWorkflow sans objectif' }
    const runtime = s(args.runtime)
    // MAS when explicitly asked OR when the runtime-router judges the goal complex.
    if (runtime === 'mas' || createMasPlanForGoal(goal)) {
      const mas = await createMasPlan({
        goal,
        domain: s(args.domain, ctx.module ?? ''),
        autonomy: ctx.mode,
        autoStart: true,
      })
      if (mas.ok && mas.planId) {
        return {
          ok: true,
          detail: `Plan MAS lancé (${mas.subtaskCount ?? 0} sous-tâche(s))`,
          output: { plan_id: mas.planId, runtime: 'mas' },
          card: { kind: 'mas', title: `Plan MAS — ${goal.slice(0, 60)}`, source: 'mas', planId: mas.planId },
        }
      }
      if (mas.missing) {
        // MAS not deployed → fall through to the durable single-task engine.
      } else if (!mas.ok) {
        return { ok: false, detail: `MAS : ${mas.error ?? 'échec de création du plan'}` }
      }
    }
    const task = await createAgenticTask({ goal, domain: s(args.domain, ctx.module ?? '') })
    if (!task) return { ok: false, detail: 'Impossible de lancer la tâche en arrière-plan.' }
    return {
      ok: true,
      detail: 'Tâche lancée en arrière-plan — suivez-la dans le bac à tâches.',
      output: { task_id: task.id, runtime: 'durable' },
    }
  },
}

const stopWorkflow: CompanionTool = {
  name: 'stopWorkflow',
  description: 'Arrêter une tâche / un plan en cours',
  argsSchema: [
    { name: 'task_id', type: 'string' },
    { name: 'plan_id', type: 'string' },
  ],
  mode: 'destructive',
  hitl: true,
  surfaces: ['web'],
  group: 'core',
  run: async (args): Promise<ToolRunResult> => {
    const planId = s(args.plan_id)
    if (planId) {
      const r = await controlMasPlan(planId, 'cancel')
      return r.ok
        ? { ok: true, detail: `Plan MAS annulé`, output: { plan_id: planId, status: r.status } }
        : { ok: false, detail: `Annulation MAS : ${r.error ?? 'échec'}` }
    }
    const taskId = s(args.task_id ?? args.id)
    if (!taskId) return { ok: false, detail: 'stopWorkflow sans identifiant' }
    // Cancel via the tray cancel cascade (tray → durable DELETE).
    const ok = await cancelCompanionTask({
      id: taskId,
      title: '',
      status: 'RUNNING',
      progressPct: null,
      creditsUsed: null,
      source: 'agentic',
      cancellable: true,
      backendTaskId: taskId,
    })
    return ok
      ? { ok: true, detail: 'Tâche annulée', output: { task_id: taskId } }
      : { ok: false, detail: 'Impossible d’annuler la tâche.' }
  },
}

// ── queryData ─────────────────────────────────────────────────────────────────

const queryData: CompanionTool = {
  name: 'queryData',
  description: 'Interroger vos données en langage naturel',
  argsSchema: [{ name: 'question', type: 'string', required: true }],
  mode: 'read',
  hitl: false,
  surfaces: ['web'],
  group: 'core',
  run: async (args): Promise<ToolRunResult> => {
    const question = s(args.question ?? args.query ?? args.q)
    if (!question) return { ok: false, detail: 'queryData sans question' }
    const r = await queryDataSvc(question)
    if (!r.ok) return { ok: false, detail: `Requête : ${r.error ?? 'source de données indisponible'}` }
    return {
      ok: true,
      detail: r.summary || `${r.rows.length} résultat(s) (${r.source})`,
      output: { summary: r.summary, rows: r.rows, count: r.rows.length, top: r.rows[0] ?? null },
      card: {
        kind: 'query',
        title: 'Réponse données',
        source: r.source,
        summary: r.summary,
        table: r.rows.slice(0, 25),
      },
    }
  },
}

// ── recallTasks ───────────────────────────────────────────────────────────────

const recallTasks: CompanionTool = {
  name: 'recallTasks',
  description: 'Lister mes tâches Companion (bac à tâches)',
  argsSchema: [{ name: 'status', type: 'string', description: 'Filtre optionnel (RUNNING, DONE…)' }],
  mode: 'read',
  hitl: false,
  surfaces: ['web'],
  group: 'core',
  run: async (args): Promise<ToolRunResult> => {
    const { tasks, source } = await fetchCompanionTasks()
    const filter = s(args.status).toUpperCase()
    const filtered = filter ? tasks.filter((t) => t.status === filter) : tasks
    return {
      ok: true,
      detail: `${filtered.length} tâche(s) (${source})`,
      output: { count: filtered.length, tasks: filtered.map((t) => ({ id: t.id, title: t.title, status: t.status })) },
      card: {
        kind: 'info',
        title: 'Bac à tâches',
        source,
        table: filtered.slice(0, 25).map((t) => ({ titre: t.title, statut: t.status, crédits: t.creditsUsed })),
      },
    }
  },
}

// ── notify ────────────────────────────────────────────────────────────────────

const notify: CompanionTool = {
  name: 'notify',
  description: 'Envoyer une notification',
  argsSchema: [
    { name: 'title', type: 'string', required: true },
    { name: 'body', type: 'string' },
  ],
  mode: 'write',
  hitl: false,
  surfaces: ['web'],
  group: 'core',
  run: async (args): Promise<ToolRunResult> => {
    const title = s(args.title ?? args.msg ?? args.message)
    if (!title) return { ok: false, detail: 'notify sans titre' }
    const r = await sendNotification({ title, body: s(args.body ?? args.detail, title) })
    return r.ok
      ? { ok: true, detail: 'Notification envoyée', output: { sent: true } }
      : { ok: false, detail: `Notification : ${r.error ?? 'service indisponible'}` }
  },
}

// ── analyzeCurrentPage ────────────────────────────────────────────────────────

const analyzeCurrentPage: CompanionTool = {
  name: 'analyzeCurrentPage',
  description: 'Résumer le contexte de la page courante',
  argsSchema: [],
  mode: 'read',
  hitl: false,
  surfaces: ['web'],
  group: 'core',
  run: async (_args, ctx): Promise<ToolRunResult> => {
    const page = readPageContext()
    const entity = ctx.entity
    const rows: Record<string, unknown> = {
      route: page.current_route,
      contexte: page.page_context ?? '—',
      formulaire_actif: page.active_form_id ?? '—',
    }
    if (entity) {
      rows['entité'] = `${entity.entityType} · ${entity.entityId}`
    }
    return {
      ok: true,
      detail: `Contexte : ${page.current_route}`,
      output: { page, entity: entity ?? null },
      card: { kind: 'info', title: 'Contexte de la page', rows },
    }
  },
}

export const CORE_TOOLS: CompanionTool[] = [
  navigate,
  openForm,
  fillForm,
  createRecord,
  updateRecord,
  startWorkflow,
  stopWorkflow,
  queryData,
  recallTasks,
  notify,
  analyzeCurrentPage,
]
